import { useEffect, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import api, { eur, num } from "@/lib/api";
import { openPdfBlob } from "@/lib/pdf";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Loader2, FileSpreadsheet, RotateCcw, ChevronLeft, ChevronRight, ChevronDown, ChevronRight as ChevRight, FileDown } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { RichQuoteDetail } from "@/components/RichQuoteDetail";

const PAGE = 100;
const fmtDate = (d) => { if (!d) return "—"; const s = String(d).slice(0, 10); return s.split("-").reverse().join("/"); };const eurN = (v) => (v == null ? "—" : eur(v));
const pct = (v) => (v == null || v === "" ? "—" : `${Number(v).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`);

export default function Preventivi() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [agencies, setAgencies] = useState([]);
  const [f, setF] = useState({ agency_id: "all", from_month: "", to_month: "", search: "", stato: "all" });
  const [page, setPage] = useState(0);
  const [expanded, setExpanded] = useState(null);
  const { user } = useAuth();
  const [sconfinati, setSconfinati] = useState(new Set());
  const canMargin = ["admin", "back_office", "commercial_manager"].includes(user?.role);

  const seqRef = useRef(0);
  const load = useCallback(() => {
    setLoading(true);
    const seq = ++seqRef.current;
    const q = new URLSearchParams({ limit: String(PAGE), offset: String(page * PAGE) });
    if (f.agency_id !== "all") q.append("agency_id", f.agency_id);
    if (f.stato !== "all") q.append("stato", f.stato);
    if (f.from_month) q.append("from_month", f.from_month);
    if (f.to_month) q.append("to_month", f.to_month);
    if (f.search.trim()) q.append("search", f.search.trim());
    api.get(`/quotes?${q.toString()}`)
      .then((r) => { if (seq === seqRef.current) setData(r.data); })
      .finally(() => { if (seq === seqRef.current) setLoading(false); });
  }, [f, page]);

  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load]);
  useEffect(() => { api.get("/agencies").then((r) => setAgencies(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    if (["admin", "back_office"].includes(user?.role)) api.get("/crm-quotes/affidamenti-sconfinati").then((r) => setSconfinati(new Set(r.data.ids || []))).catch(() => {});
  }, [user]);

  const setField = (k) => (v) => { setPage(0); setExpanded(null); setF((s) => ({ ...s, [k]: v })); };
  const reset = () => { setPage(0); setExpanded(null); setF({ agency_id: "all", from_month: "", to_month: "", search: "", stato: "all" }); };

  const exportCsv = async () => {
    const q = new URLSearchParams({ limit: "100000", offset: "0" });
    if (f.agency_id !== "all") q.append("agency_id", f.agency_id);
    if (f.stato !== "all") q.append("stato", f.stato);
    if (f.from_month) q.append("from_month", f.from_month);
    if (f.to_month) q.append("to_month", f.to_month);
    if (f.search.trim()) q.append("search", f.search.trim());
    const r = await api.get(`/quotes?${q.toString()}`);
    const cols = ["numero_preventivo", "date", "cliente", "agency_name", "tipologia", "stato", "kwp", "importo_pannelli_eur", "importo_accessori_eur", "importo_eur", ...(canMargin ? ["margine_commessa_pct", "margine_eur"] : []), "modalita_pagamento"];
    const head = ["N° Preventivo", "Data", "Cliente", "Agenzia", "Tipologia", "Stato", "kWp", "Pannelli €", "Accessori/Servizi €", "Totale €", ...(canMargin ? ["Margine %", "Margine €"] : []), "Pagamento"];
    const rows = (r.data.quotes || []).map((o) => cols.map((c) => `"${(o[c] ?? "").toString().replace(/"/g, '""')}"`).join(","));
    const blob = new Blob([[head.join(","), ...rows].join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "archivio_preventivi_torri.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const t = data?.totals;
  const total = data?.total || 0;
  const pages = Math.max(1, Math.ceil(total / PAGE));
  const COLS = canMargin ? 11 : 10;

  return (
    <div className="space-y-6" data-testid="quotes-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-black tracking-tight">Archivio Preventivi</h1>
          <p className="text-sm text-muted-foreground">Storico preventivi Torri · aggiornato automaticamente insieme agli ordini. Clicca una riga per il dettaglio articoli, margine e PDF.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={exportCsv} data-testid="quotes-export-btn">
            <FileSpreadsheet className="mr-2 h-4 w-4" /> Esporta CSV
          </Button>
        </div>
      </div>

      <Card className="p-4">
        <div className="flex flex-wrap items-end gap-2" data-testid="quotes-filters">
          <div className="flex-1 min-w-[200px]">
            <label className="mb-1 block text-xs font-semibold text-muted-foreground">Cerca cliente / agenzia</label>
            <Input value={f.search} onChange={(e) => setField("search")(e.target.value)} placeholder="Nome cliente o agenzia…" data-testid="quotes-search" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold text-muted-foreground">Agenzia</label>
            <Select value={f.agency_id} onValueChange={setField("agency_id")}>
              <SelectTrigger className="w-[190px]" data-testid="quotes-filter-agency"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">Tutte le agenzie</SelectItem>{agencies.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold text-muted-foreground">Stato</label>
            <Select value={f.stato} onValueChange={setField("stato")}>
              <SelectTrigger className="w-[150px]" data-testid="quotes-filter-stato"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti</SelectItem>
                <SelectItem value="Bozza">Bozza</SelectItem>
                <SelectItem value="In corso">In corso</SelectItem>
                <SelectItem value="Confermato">Confermato</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold text-muted-foreground">Da mese</label>
            <Input type="month" value={f.from_month} onChange={(e) => setField("from_month")(e.target.value)} className="w-[150px]" data-testid="quotes-from-month" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold text-muted-foreground">A mese</label>
            <Input type="month" value={f.to_month} onChange={(e) => setField("to_month")(e.target.value)} className="w-[150px]" data-testid="quotes-to-month" />
          </div>
          <Button variant="ghost" size="sm" onClick={reset} data-testid="quotes-reset"><RotateCcw className="mr-1.5 h-4 w-4" /> Azzera</Button>
        </div>
      </Card>

      {t && (
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
          <Card className="p-4" data-testid="quotes-total-count"><div className="text-xs font-semibold uppercase text-muted-foreground">Preventivi</div><div className="mt-1 font-heading text-2xl font-black">{num(t.count)}</div></Card>
          <Card className="p-4" data-testid="quotes-total-kwp"><div className="text-xs font-semibold uppercase text-muted-foreground">kWp totali</div><div className="mt-1 font-heading text-2xl font-black">{num(Math.round(t.kwp))}</div></Card>
          <Card className="p-4" data-testid="quotes-total-pannelli"><div className="text-xs font-semibold uppercase text-muted-foreground">Pannelli</div><div className="mt-1 font-heading text-2xl font-black">{eurN(t.importo_pannelli_eur)}</div></Card>
          <Card className="p-4" data-testid="quotes-total-accessori"><div className="text-xs font-semibold uppercase text-muted-foreground">Accessori/Servizi</div><div className="mt-1 font-heading text-2xl font-black">{eurN(t.importo_accessori_eur)}</div></Card>
          <Card className="p-4" data-testid="quotes-total-importo"><div className="text-xs font-semibold uppercase text-muted-foreground">Totale</div><div className="mt-1 font-heading text-2xl font-black">{eur(t.importo_eur)}</div></Card>
        </div>
      )}

      <Card className="p-0" data-testid="quotes-table-card">
        {loading ? (
          <div className="flex h-40 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8"></TableHead>
                  <TableHead>N° Preventivo</TableHead>
                  <TableHead>Data</TableHead>
                  <TableHead>Cliente</TableHead>
                  <TableHead>Agenzia</TableHead>
                  <TableHead>Tipologia</TableHead>
                  <TableHead>Stato</TableHead>
                  <TableHead className="text-right">kWp</TableHead>
                  <TableHead className="text-right">Totale</TableHead>
                  {canMargin && <TableHead className="text-right">Margine %</TableHead>}
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(data?.quotes || []).map((o) => (
                  <>
                    <TableRow key={o.id} data-testid={`quote-row-${o.id}`} className="cursor-pointer" onClick={() => setExpanded(expanded === o.id ? null : o.id)}>
                      <TableCell className="text-muted-foreground">{expanded === o.id ? <ChevronDown className="h-4 w-4" /> : <ChevRight className="h-4 w-4" />}</TableCell>
                      <TableCell className="whitespace-nowrap font-mono text-xs font-semibold">{o.numero_preventivo || "—"}</TableCell>
                      <TableCell className="whitespace-nowrap font-mono text-xs">{fmtDate(o.date)}</TableCell>
                      <TableCell className="font-semibold">
                        {o.partner_id
                          ? <Link to={`/partners/${o.partner_id}`} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="hover:text-primary hover:underline" data-testid={`quote-client-link-${o.id}`} title="Apri anagrafica cliente (nuova scheda)">{o.cliente || "—"}</Link>
                          : (o.cliente || "—")}
                        {o.partner_id && sconfinati.has(o.partner_id) && <Badge className="ml-2 bg-red-600 text-white text-[10px]" data-testid={`quote-sconfino-${o.id}`}>Oltre fido</Badge>}
                      </TableCell>
                      <TableCell className="text-muted-foreground">{o.agency_name || "—"}</TableCell>
                      <TableCell className="text-muted-foreground">{o.tipologia || "—"}</TableCell>
                      <TableCell><Badge variant="outline" className="text-[10px]">{o.stato || "—"}</Badge></TableCell>
                      <TableCell className="text-right font-mono">{num(Math.round(o.kwp || 0))}</TableCell>
                      <TableCell className="text-right font-mono font-semibold">{eur(o.importo_eur)}</TableCell>
                      {canMargin && <TableCell className="text-right font-mono">{pct(o.margine_commessa_pct)}</TableCell>}
                      <TableCell className="text-right">{o.has_pdf && <FileDown className="ml-auto h-4 w-4 text-primary" />}</TableCell>
                    </TableRow>
                    {expanded === o.id && (
                      <TableRow data-testid={`quote-detail-row-${o.id}`}>
                        <TableCell colSpan={COLS} className="bg-muted/30 p-0">
                          {o.source === "crm"
                            ? <RichQuoteDetail source="crm" id={o.id} canMargin={canMargin} hasPdf={o.has_pdf} />
                            : <RichQuoteDetail source="torri" id={o.id} canMargin={canMargin} hasPdf={o.has_pdf} />}
                        </TableCell>
                      </TableRow>
                    )}
                  </>
                ))}
                {(!data?.quotes || data.quotes.length === 0) && (
                  <TableRow><TableCell colSpan={COLS} className="py-12 text-center text-muted-foreground">Nessun preventivo per i filtri selezionati.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        )}
        {total > PAGE && (
          <div className="flex items-center justify-between border-t border-border px-4 py-3 text-sm">
            <span className="text-muted-foreground">Pagina {page + 1} di {pages} · {num(total)} preventivi</span>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled={page === 0} onClick={() => { setExpanded(null); setPage((p) => p - 1); }} data-testid="quotes-prev"><ChevronLeft className="h-4 w-4" /></Button>
              <Button variant="outline" size="sm" disabled={page + 1 >= pages} onClick={() => { setExpanded(null); setPage((p) => p + 1); }} data-testid="quotes-next"><ChevronRight className="h-4 w-4" /></Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
